import { normalizePubNubTimetoken } from './pubnubTimetoken';

export const RECALL_MESSAGE_EVENT = 'RECALL_MESSAGE';

const DEFAULT_PUBNUB_SYSTEM_BACKEND_ID = 'system-backend';

const runtimeTrustedRecallPublishers = new Set<string>();

function parseTrustedRecallPublisherEnv(): string[] {
  const raw = process.env.REACT_APP_PUBNUB_SYSTEM_BACKEND_ID?.trim();
  if (!raw) return [];

  return raw
    .split(',')
    .map((value) => value.trim())
    .filter(Boolean);
}

export function registerTrustedRecallPublisher(
  publisherId: string | undefined | null,
): void {
  const normalized = publisherId?.trim();
  if (!normalized) return;
  runtimeTrustedRecallPublishers.add(normalized);
}

export function getTrustedRecallPublishers(): Set<string> {
  const publishers = new Set<string>(runtimeTrustedRecallPublishers);

  for (const publisherId of parseTrustedRecallPublisherEnv()) {
    publishers.add(publisherId);
  }

  if (publishers.size === 0) {
    publishers.add(DEFAULT_PUBNUB_SYSTEM_BACKEND_ID);
  }

  return publishers;
}

export function getPubNubSystemBackendId(): string {
  const publishers = Array.from(getTrustedRecallPublishers());
  return publishers[publishers.length - 1] ?? DEFAULT_PUBNUB_SYSTEM_BACKEND_ID;
}

export function isTrustedRecallPublisher(
  publisher: string | undefined | null,
): boolean {
  if (!publisher) return false;
  return getTrustedRecallPublishers().has(publisher);
}

export type RecallMessageNotification = {
  event?: string;
  channel?: string;
  timetoken?: string | number;
};

export function isMessageRecalledByPubNub(
  actions?: Record<string, Record<string, unknown>>,
): boolean {
  const deleted = actions?.deleted;
  if (!deleted || typeof deleted !== 'object') return false;
  return Object.keys(deleted).length > 0;
}

export function getRecallMessageNotification(
  payload: unknown,
): RecallMessageNotification | null {
  if (!payload || typeof payload !== 'object') return null;
  const notification = payload as RecallMessageNotification;
  if (notification.event !== RECALL_MESSAGE_EVENT) return null;
  if (notification.timetoken == null || !notification.channel) return null;
  return notification;
}

export function getTrustedRecallMessageNotification(
  payload: unknown,
  publisher: string | undefined | null,
): RecallMessageNotification | null {
  const notification = getRecallMessageNotification(payload);
  if (!notification) return null;
  if (!isTrustedRecallPublisher(publisher)) return null;
  return notification;
}

export function isRelevantRecallForChat(params: {
  notification: RecallMessageNotification;
  eventChannel: string;
  chatChannel: string;
  currentUserUuid?: string | null;
}): boolean {
  const { notification, eventChannel, chatChannel, currentUserUuid } = params;

  // The recall payload must target the open chat, regardless of which channel
  // delivered it. Otherwise a trusted recall for another channel arriving on the
  // chat channel could hide an unrelated timetoken in the active thread.
  if (notification.channel !== chatChannel) return false;

  // And it must arrive via a channel that legitimately carries this chat's
  // events: the chat channel itself or the current user's notification channel.
  return eventChannel === chatChannel || eventChannel === currentUserUuid;
}

export function isPubNubMessageDeletedAction(event: {
  channel: string;
  event?: string;
  data?: { type?: string };
  messageTimetoken?: string | number;
}): event is {
  channel: string;
  event: string;
  data: { type: string };
  messageTimetoken: string | number;
} {
  return (
    event.event === 'added' &&
    event.data?.type === 'deleted' &&
    event.messageTimetoken != null
  );
}

export function removeMessageByTimetoken<T extends { timetoken: string | number }>(
  messages: T[],
  timetoken: string | number,
): T[] {
  const normalized = normalizePubNubTimetoken(timetoken);
  return messages.filter(
    (msg) => normalizePubNubTimetoken(msg.timetoken) !== normalized,
  );
}

export function applyMessageRecallForMale<
  T extends { timetoken: string | number },
>(
  recalledTimetokens: Set<string>,
  setMessages: (update: (prev: T[]) => T[]) => void,
  timetoken: string | number,
): void {
  const normalized = normalizePubNubTimetoken(timetoken);
  recalledTimetokens.add(normalized);
  setMessages((prev) => removeMessageByTimetoken(prev, timetoken));
}

export function shouldHideMessageFromHistory(message: {
  message?: unknown;
  actions?: Record<string, Record<string, unknown>>;
  timetoken: string | number;
  recalledTimetokens: Set<string>;
}): boolean {
  if (getRecallMessageNotification(message.message)) return true;
  if (isMessageRecalledByPubNub(message.actions)) return true;
  return message.recalledTimetokens.has(
    normalizePubNubTimetoken(message.timetoken),
  );
}
